import type { IMapChildren } from './children';
import { z } from 'zod';
import type { Getter, Setter } from '@dhmk/zustand-lens';
import type { Draft } from 'immer';
import type { EntrySlice } from '.';
import {
  type EntryItemType,
  EntryType,
  type TreeNode,
} from '@morph-mapper/types';
import type { StructureBlockIndex } from '@morph-mapper/node-logic';
import { match } from 'ts-pattern';

// TODO: Seperate out the EntryItemType from the EntryType

export interface ICodeEntry extends IEntry {
  setCode: (code: string) => void;
  getCode: () => string | undefined;
}

const createCodeEntryAPI = (
  _set: Setter<Draft<EntrySlice>>,
  get: Getter<Draft<EntrySlice>>,
  id: string
): Omit<ICodeEntry, keyof IEntry> => {
  return {
    setCode: (code) => get().setUnderlyingValue(id, code),
    getCode: () => get().getUnderlyingValue(id),
  };
};

export interface IBooleanEntry extends IEntry {
  setBoolean: (bool: boolean) => void;
  getBoolean: () => boolean | undefined;
}

const createBooleanEntryAPI = (
  _set: Setter<Draft<EntrySlice>>,
  get: Getter<Draft<EntrySlice>>,
  id: string
): Omit<IBooleanEntry, keyof IEntry> => {
  return {
    setBoolean: (literal) => get().setUnderlyingValue(id, literal),
    getBoolean: () => get().getUnderlyingValue(id),
  };
};

export interface ISimpleEntry extends IEntry {
  setLiteral: (literal: string | undefined) => void;
  getLiteral: () => string | undefined;
}

const createSimpleEntryAPI = (
  _set: Setter<Draft<EntrySlice>>,
  get: Getter<Draft<EntrySlice>>,
  id: string
): Omit<ISimpleEntry, keyof IEntry> => {
  return {
    setLiteral: (literal) => get().setUnderlyingValue(id, literal),
    getLiteral: () => get().getUnderlyingValue(id),
  };
};

export interface ICellEntry extends IEntry {
  getColumn: () => number | undefined;
  setColumn: (column: number | undefined) => void;
}

const createCellEntryAPI = (
  _set: Setter<Draft<EntrySlice>>,
  get: Getter<Draft<EntrySlice>>,
  id: string
): Omit<ICellEntry, keyof IEntry> => {
  return {
    getColumn: () => get().getUnderlyingValue(id),
    setColumn: (column) => get().setUnderlyingValue(id, column),
  };
};

export interface IGraphEntry extends IEntry {
  linkGraph: (graphId: string) => void;
  getGraphId: () => string | undefined;
}

const createGraphEntryAPI = (
  _set: Setter<Draft<EntrySlice>>,
  get: Getter<Draft<EntrySlice>>,
  id: string
): Omit<IGraphEntry, keyof IEntry> => {
  return {
    linkGraph: (graphId) => get().linkGraph(id, graphId),
    getGraphId: () => get().getUnderlyingValue(id),
  };
};

export interface IInternalEntry extends IEntry {}

export interface IMapEntry extends IEntry {
  children: () => IMapChildren;

  linkChild: (childId: string) => void;
  getOutputType: () => StructureBlockIndex;
  setOutputType: (outputType: StructureBlockIndex) => void;

  getFormerOutputType: () => StructureBlockIndex;
  setFormerOutputType: (outputType: StructureBlockIndex) => void;
}

const createMapEntryAPI = (
  _set: Setter<Draft<EntrySlice>>,
  get: Getter<Draft<EntrySlice>>,
  id: string
): Omit<IMapEntry, keyof IEntry> => {
  return {
    children: () => get().children(id),
    linkChild: (childId) => get().linkChild(id, childId),
    getOutputType: () => get().getOutputType(id),
    setOutputType: (outputType) => get().setOutputType(id, outputType),
    getFormerOutputType: () => get().getFormerOutputType(id),
    setFormerOutputType: (outputType) =>
      get().setFormerOutputType(id, outputType),
  };
};

export interface IEntry {
  getParent: () => IMapEntry | undefined;
  getById: () => TreeNode;
  getParentId: () => string | undefined;
  getAllowedTypes: () => EntryItemType[];
  getType: () => EntryType;
  setType: (type: EntryItemType) => void;
  getId: () => string;
  getKey: () => string;
  getName: () => string | undefined;
  getComputed: () => any;
  setComputed: (computed: any) => void;
  getValidation: () => z.ZodType<any, any, any> | undefined;
  getDependsOn: () => string[];
  getRequiredBy: () => string[];
  getPath: () => string[];
  getUpdateEnabled: () => boolean;
  setUpdateEnabled: (enabled: boolean) => void;
  getTemplateValue: () => any;
  setTemplateValue: (value: any) => void;
  hasValue: () => boolean;
  copy: (targetId: string) => void;
  clear: () => void;
  delete: () => void;
}

export type EntryReturnType<T extends EntryType> = T extends EntryType.Cell
  ? ICellEntry
  : T extends EntryType.Graph
  ? IGraphEntry
  : T extends EntryType.Simple
  ? ISimpleEntry
  : T extends EntryType.Map
  ? IMapEntry
  : T extends EntryType.Internal
  ? IInternalEntry
  : T extends EntryType.Boolean
  ? IBooleanEntry
  : T extends EntryType.Code
  ? ICodeEntry
  : IEntry;

export const createEntryAPI = <T extends EntryType>(
  _set: Setter<Draft<EntrySlice>>,
  get: Getter<Draft<EntrySlice>>,
  id: string,
  type?: T
): EntryReturnType<T> => {
  const baseEntry: IEntry = {
    getParent: () => get().getParent(id),
    getById: () => get().getById(id),
    getParentId: () => get().getParentId(id),
    getAllowedTypes: () => get().getAllowedTypes(id),
    getType: () => get().getType(id),
    setType: (type) => get().setType(id, type),
    getId: () => id,
    getKey: () => get().getKey(id),
    getName: () => get().getName(id),
    getComputed: () => get().getComputed(id),
    setComputed: (computed) => get().setComputed(id, computed),
    getValidation: () => get().getValidation(id),
    getDependsOn: () => get().getDependsOn(id),
    getRequiredBy: () => get().getRequiredBy(id),
    getPath: () => get().getPath(id),
    getUpdateEnabled: () => get().getUpdateEnabled(id),
    setUpdateEnabled: (enabled) => get().setUpdateEnabled(id, enabled),
    getTemplateValue: () => get().getTemplateValue(id),
    setTemplateValue: (value) => get().setTemplateValue(id, value),
    hasValue: () => get().hasValue(id),
    copy: (targetId) => get().copy(id, targetId),
    clear: () => get().clear(id),
    delete: () => get().delete(id),
  };

  if (!type) {
    return baseEntry as EntryReturnType<T>;
  }

  return match<EntryType>(type)
    .with(EntryType.Cell, () => ({
      ...baseEntry,
      ...createCellEntryAPI(_set, get, id),
    }))
    .with(EntryType.Boolean, () => ({
      ...baseEntry,
      ...createBooleanEntryAPI(_set, get, id),
    }))
    .with(EntryType.Simple, () => ({
      ...baseEntry,
      ...createSimpleEntryAPI(_set, get, id),
    }))
    .with(EntryType.Map, () => ({
      ...baseEntry,
      ...createMapEntryAPI(_set, get, id),
    }))
    .with(EntryType.Graph, () => ({
      ...baseEntry,
      ...createGraphEntryAPI(_set, get, id),
    }))
    .with(EntryType.Code, () => ({
      ...baseEntry,
      ...createCodeEntryAPI(_set, get, id),
    }))
    .with(EntryType.Internal, () => ({
      ...baseEntry,
    }))
    .exhaustive() as EntryReturnType<T>;
};
